
import React, { useEffect, useState } from "react";
import axios from "axios";
import { FaTrash, FaEdit } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddStudentModal from "../Components/AddEnrollModel.js";

const ViewStudents = () => {
  const courseList = [
    "Web Development",
    "Graphic Designing",
    "Forex Trading",
    "Digital Marketing",
    "Data Science",
    "Spoken English",
    "UI/UX Designing",
    "Python Programming",
  ];

  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState("View All");
  const [batchQuery, setBatchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showSingleDeleteDialog, setShowSingleDeleteDialog] = useState(false);
  const [studentToDelete, setStudentToDelete] = useState(null);

  // 🔹 Fetch both APIs and merge
  const fetchAllStudents = async () => {
    try {
      const [res1, res2] = await Promise.all([
        axios.get("https://expertb.codebyte.live/api/viewApprovedStudents"),
        axios.get("https://expertb.codebyte.live/api/admin/adminStudents"),
      ]);

      const data1 = (res1.data || []).map((s) => ({ ...s, source: "approved" }));
      const data2 = (res2.data || []).map((s) => ({ ...s, source: "admin" }));

      const merged = [...data1, ...data2];
      setStudents(merged);
      setFilteredStudents(merged);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch students.");
    }
  };

  useEffect(() => {
    fetchAllStudents();
  }, []);

  // 🔹 Filter by course
  const handleFilter = (e) => {
    const course = e.target.value;
    setSelectedCourse(course);

    if (course === "View All") {
      setFilteredStudents(students);
    } else {
      const filtered = students.filter(
        (s) => s.courseName?.toLowerCase() === course.toLowerCase()
      );
      setFilteredStudents(filtered);
    }
  };

  // 🔹 Edit student
  const handleEdit = (student) => {
    const modalData = {
      ...student,
      image: student.image || student.profilePic || null,
    };
    setEditingStudent(modalData);
    setIsAddModalOpen(true);
  };

  // 🔹 Delete single student (with if/else API)
  const confirmSingleDelete = (student) => {
    setStudentToDelete(student);
    setShowSingleDeleteDialog(true);
  };

  const handleSingleDeleteConfirmed = async () => {
    if (!studentToDelete?._id) return;
    try {
      if (studentToDelete.source === "admin") {
        await axios.delete(
          `https://expertb.codebyte.live/api/admin/deleteAdminStudent/${studentToDelete._id}`
        );
      } else {
        await axios.delete(
          `https://expertb.codebyte.live/api/deleteStudent/${studentToDelete._id}`
        );
      }

      await fetchAllStudents();
      toast.success("Student deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete student.");
    } finally {
      setShowSingleDeleteDialog(false);
      setStudentToDelete(null);
    }
  };

  // 🔹 Delete all students (with if/else)
  const handleDeleteAllConfirmed = async () => {
    try {
      if (students.some((s) => s.source === "admin") && students.some((s) => s.source === "approved")) {
        // dono delete karna ho
        await Promise.all([
          axios.delete("https://expertb.codebyte.live/api/deleteAllStudent"),
          axios.delete("https://expertb.codebyte.live/api/admin/deleteAllStudent"),
        ]);
      } else if (students.every((s) => s.source === "admin")) {
        await axios.delete("https://expertb.codebyte.live/api/admin/deleteAllStudent");
      } else {
        await axios.delete("https://expertb.codebyte.live/api/deleteAllStudent");
      }

      fetchAllStudents();
      toast.success("All students deleted successfully!");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete all students.");
    } finally {
      setShowDeleteDialog(false);
    }
  };

  return (
    <div className="px-4 py-8 max-w-screen-xl mx-auto">
      <ToastContainer />

      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-6">
        <h2 className="text-2xl font-bold text-red-600">Student Management</h2>
        <div className="flex flex-wrap gap-3 items-center">
          <select
            value={selectedCourse}
            onChange={handleFilter}
            className="border border-gray-300 px-4 py-2 rounded shadow"
          >
            <option value="View All">View All</option>
            {courseList.map((course) => (
              <option key={course} value={course}>
                {course}
              </option>
            ))}
          </select>
          {selectedCourse !== "View All" && (
            <button
              onClick={() => {
                setFilteredStudents(students);
                setSelectedCourse("View All");
              }}
              className="px-3 py-2 bg-gray-200 rounded hover:bg-gray-300 text-sm"
            >
              Reset
            </button>
          )}

          {/* Batch Dropdown */}
          <select
            value={batchQuery}
            onChange={(e) => {
              const selected = e.target.value;
              setBatchQuery(selected);

              if (selected === "") {
                setFilteredStudents(students);
                setSelectedCourse("View All");
              } else {
                const numericBatch = parseInt(selected, 10);
                const result = students.filter(
                  (s) => parseInt(s.batch, 10) === numericBatch
                );
                setFilteredStudents(result);
                setSelectedCourse(`Batch: ${numericBatch}`);
              }
            }}
            className="px-4 py-2 border rounded"
          >
            <option value="">Select Batch</option>
            {Array.from({ length: 100 }, (_, i) => i + 1).map((batch) => (
              <option key={batch} value={batch}>
                Batch {batch}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Total Students */}
      <div className="mb-4">
        <p className="text-lg font-semibold text-green-700">
          Total Students: {filteredStudents.length}
        </p>
      </div>

      {/* Table */}
      {filteredStudents.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white border shadow-md rounded-lg">
            <thead className="bg-gray-100">
              <tr className="text-left">
                <th className="px-4 py-2 border">#</th>
                <th className="px-4 py-2 border">Profile</th>
                <th className="px-4 py-2 border">Name</th>
                <th className="px-4 py-2 border">Phone</th>
                <th className="px-4 py-2 border">Course</th>
                <th className="px-4 py-2 border">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student, index) => (
                <tr key={student._id || index} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-2 border">{index + 1}</td>
                  <td className="px-4 py-2 border">
                    {student.image || student.profilePic ? (
                      <img
                        src={`https://expertb.codebyte.live/uploads/${student.image || student.profilePic}`}
                        alt="Profile"
                        className="w-12 h-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-12 h-12 rounded-full bg-gray-300 flex items-center justify-center text-gray-600">
                        N/A
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2 border">{student.studentName}</td>
                  <td className="px-4 py-2 border">{student.phone1}</td>
                  <td className="px-4 py-2 border">{student.courseName}</td>
                  <td className="px-4 py-2 border space-x-2">
                    <button
                      onClick={() => handleEdit(student)}
                      className="text-yellow-600 hover:text-yellow-800"
                    >
                      <FaEdit />
                    </button>
                    <button
                      onClick={() => confirmSingleDelete(student)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-center text-gray-500 mt-10 text-lg">No students found.</p>
      )}

      {/* Delete All Button */}
      {filteredStudents.length > 0 && (
        <div className="mt-6 text-center">
          <button
            onClick={() => setShowDeleteDialog(true)}
            className="bg-red-600 text-white px-6 py-2 rounded hover:bg-red-700"
          >
            Delete All Students
          </button>
        </div>
      )}

      {/* Delete All Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
            <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete all students? This action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleDeleteAllConfirmed}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Delete All
              </button>
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Single Delete Dialog */}
      {showSingleDeleteDialog && studentToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-gray-900">Confirm Deletion</h3>
            <p className="mt-2 text-sm text-gray-600">
              Are you sure you want to delete{" "}
              <span className="font-medium text-red-600">{studentToDelete.studentName}</span>?
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={handleSingleDeleteConfirmed}
                className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700"
              >
                Delete
              </button>
              <button
                onClick={() => {
                  setShowSingleDeleteDialog(false);
                  setStudentToDelete(null);
                }}
                className="bg-gray-200 text-gray-800 px-4 py-2 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <AddStudentModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingStudent(null);
          fetchAllStudents();
        }}
        studentData={editingStudent}
      />
    </div>
  );
};

export default ViewStudents;
